// Chiffre d'affaires — revenue analytics with period selector and breakdown tables
import { useState, useMemo } from "react";
import { TrendingUp, AlertCircle } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { KPICard } from "@/components/admin/shared/KPICard";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useRevenue } from "@/hooks/admin/useFinance";
import type { RevenuePeriod, RevenuePoint } from "@/lib/admin-api/finance";

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(amount: number | null | undefined): string {
  const n = Number(amount);
  if (isNaN(n)) return "—";
  return `${n.toLocaleString("fr-SN")} FCFA`;
}

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Period selector
// ---------------------------------------------------------------------------

const PERIODS: readonly { readonly value: RevenuePeriod; readonly label: string }[] = [
  { value: "day", label: "Jour" },
  { value: "week", label: "Semaine" },
  { value: "month", label: "Mois" },
  { value: "quarter", label: "Trimestre" },
  { value: "year", label: "Année" },
];

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Revenue() {
  useAdminPageTitle("Revenus");
  const [period, setPeriod] = useState<RevenuePeriod>("month");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const { data, isLoading, isError, refetch } = useRevenue(
    period,
    dateFrom || undefined,
    dateTo || undefined,
  );

  const kpis = data?.kpis;
  const series = data?.series ?? [];
  const byRegion = data?.by_region ?? [];
  const byBrand = data?.by_brand ?? [];

  const totalRevenue = useMemo(
    () => byRegion.reduce((sum, r) => sum + r.amount_fcfa, 0),
    [byRegion],
  );

  const seriesColumns: readonly ColumnDef<RevenuePoint>[] = useMemo(
    () => [
      {
        id: "date",
        header: "Date",
        cell: (row) => (
          <span className="text-sm font-medium text-stone-900">
            {formatDate(row.date)}
          </span>
        ),
      },
      {
        id: "revenue_fcfa",
        header: "CA (FCFA)",
        cell: (row) => (
          <span className="font-medium text-stone-900">
            {formatFcfa(row.revenue_fcfa)}
          </span>
        ),
      },
      {
        id: "orders",
        header: "Commandes",
        cell: (row) => (
          <span className="text-sm text-stone-700">
            {(row.orders ?? 0).toLocaleString("fr-SN")}
          </span>
        ),
      },
      {
        id: "avg_order_fcfa",
        header: "Panier moyen",
        cell: (row) => (
          <span className="text-sm text-stone-600">
            {formatFcfa(row.avg_order_fcfa)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <TrendingUp className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Chiffre d'affaires
              </h1>
              <p className="text-sm text-stone-500">
                {isLoading ? "Chargement…" : `${series.length} point(s) de données`}
              </p>
            </div>
          </div>

          {/* Date range */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <label className="text-sm font-medium text-stone-600">Du :</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <label className="text-sm font-medium text-stone-600">Au :</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
              />
            </div>
            {(dateFrom || dateTo) && (
              <button
                type="button"
                onClick={() => { setDateFrom(""); setDateTo(""); }}
                className="text-sm text-stone-500 hover:text-stone-700"
              >
                Réinitialiser
              </button>
            )}
          </div>
        </div>

        {/* Period buttons */}
        <div className="flex flex-wrap gap-2">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPeriod(p.value)}
              className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                period === p.value
                  ? "bg-amber-600 text-white"
                  : "border border-stone-200 bg-white text-stone-700 hover:bg-stone-50"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les données de chiffre d'affaires.</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="ml-auto rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* KPIs */}
        {!isError && (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KPICard
              label="CA total"
              value={kpis?.total_fcfa ?? 0}
              format="currency"
              icon={<TrendingUp className="h-4 w-4" />}
              trend={
                kpis
                  ? {
                      value: Math.abs(kpis.growth_percent),
                      direction: kpis.growth_percent >= 0 ? "up" : "down",
                    }
                  : undefined
              }
            />
            <KPICard
              label="Croissance"
              value={kpis?.growth_percent ?? 0}
              format="percent"
              icon={<TrendingUp className="h-4 w-4" />}
            />
            <KPICard
              label="Commandes"
              value={kpis?.orders_count ?? 0}
              format="number"
              icon={<TrendingUp className="h-4 w-4" />}
            />
            <KPICard
              label="Panier moyen"
              value={kpis?.avg_order_fcfa ?? 0}
              format="currency"
              icon={<TrendingUp className="h-4 w-4" />}
            />
          </div>
        )}

        {!isError && kpis && (
          <div className="grid grid-cols-1 gap-3 rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 sm:grid-cols-2">
            <p className="text-sm text-stone-600">
              Meilleure région :{" "}
              <span className="font-semibold text-stone-900">{kpis.top_region}</span>
            </p>
            <p className="text-sm text-stone-600">
              Meilleur produit :{" "}
              <span className="font-semibold text-stone-900">{kpis.top_product}</span>
            </p>
          </div>
        )}

        {/* Breakdowns */}
        {!isError && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* By region */}
            <div className="rounded-lg border border-stone-200 bg-white">
              <div className="border-b border-stone-100 px-4 py-3">
                <h2 className="text-sm font-semibold text-stone-800">CA par région</h2>
              </div>
              {!isLoading && byRegion.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-stone-400">
                  Aucune donnée par région
                </div>
              ) : (
                <div className="divide-y divide-stone-50">
                  {isLoading
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="flex items-center justify-between px-4 py-3">
                          <div className="h-4 w-24 animate-pulse rounded bg-stone-100" />
                          <div className="h-4 w-28 animate-pulse rounded bg-stone-100" />
                        </div>
                      ))
                    : byRegion.map((r) => {
                        const pct = totalRevenue > 0 ? (r.amount_fcfa / totalRevenue) * 100 : 0;
                        return (
                          <div key={r.region} className="flex items-center justify-between px-4 py-3">
                            <span className="text-sm font-medium text-stone-800">{r.region}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-stone-500">{pct.toFixed(1)}%</span>
                              <span className="text-sm font-semibold text-stone-900">
                                {formatFcfa(r.amount_fcfa)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                </div>
              )}
            </div>

            {/* By brand */}
            <div className="rounded-lg border border-stone-200 bg-white">
              <div className="border-b border-stone-100 px-4 py-3">
                <h2 className="text-sm font-semibold text-stone-800">CA par marque</h2>
              </div>
              {!isLoading && byBrand.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-stone-400">
                  Aucune donnée par marque
                </div>
              ) : (
                <div className="divide-y divide-stone-50">
                  {isLoading
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="flex items-center justify-between px-4 py-3">
                          <div className="h-4 w-24 animate-pulse rounded bg-stone-100" />
                          <div className="h-4 w-28 animate-pulse rounded bg-stone-100" />
                        </div>
                      ))
                    : byBrand.map((b) => {
                        const pct = totalRevenue > 0 ? (b.amount_fcfa / totalRevenue) * 100 : 0;
                        return (
                          <div key={b.brand} className="flex items-center justify-between px-4 py-3">
                            <span className="text-sm font-medium text-stone-800">{b.brand}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-stone-500">{pct.toFixed(1)}%</span>
                              <span className="text-sm font-semibold text-stone-900">
                                {formatFcfa(b.amount_fcfa)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Time series table */}
        {!isError && (
          <>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-stone-800">Série temporelle</h2>
            </div>
            {!isLoading && series.length === 0 ? (
              <EmptyState
                icon={<TrendingUp className="h-6 w-6" />}
                title="Aucune donnée"
                description="Sélectionnez une autre période ou ajustez les filtres."
              />
            ) : (
              <AdminDataTable
                data={series}
                columns={seriesColumns}
                isLoading={isLoading}
              />
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
